<template>
    <div class="flex flex-col px-2 pb-2 gap-3 bg-gray-100 rounded-lg border border-gray-200 shadow-sm">
        <div class="flex justify-between px-2 text-gray-500"></div>
        <div class="flex flex-col gap-2 p-2 bg-white rounded-md shadow-sm">
            <div
                v-for="(filter, filterIndex) in filters"
                :key="filter.field"
                class="flex-nowrap flex items-start justify-between text-sm cursor-pointer hover:bg-gray-100 rounded-md px-2 py-1 min-w-0 w-full"
                @mouseover="setIconVisibility(filterIndex, true)"
                @mouseleave="setIconVisibility(filterIndex, false)"
            >
                <div class="flex-1 min-w-0 flex flex-col gap-0.5">
                    <!-- Row 1: Name -->
                    <span class="text-gray-700 font-semibold truncate">
                        {{ filter.displayName }}
                    </span>
                    <!-- Row 2: Rule -->
                    <span v-if="filter.field !== 'doNotDisturb'" class="text-gray-500">
                        {{ displayFilterRule(filter.rule) }}
                    </span>
                    <!-- Row 3: Value(s) -->
                    <div class="flex flex-wrap gap-2 mt-0.5">
                        <template v-if="filter.displayName === 'Tags'">
                            <GgmsChip
                                v-for="tag in getTagsByFilterIds(filter)"
                                :key="tag._id"
                                :bgColor="cachedTags[tag._id]?.backgroundColor"
                                :textColor="cachedTags[tag._id]?.foregroundColor"
                                :isRemovable="false"
                                >{{ tag.displayName }}</GgmsChip
                            >
                        </template>
                        <template v-else-if="filter.displayName === 'Origins'">
                            <GgmsChip
                                v-for="origin in getOriginsByFilterIds(filter)"
                                :key="origin._id"
                                :bgColor="cachedOrigins[origin._id]?.backgroundColor"
                                :textColor="cachedOrigins[origin._id]?.foregroundColor"
                                :isRemovable="false"
                                >{{ origin.displayName }}</GgmsChip
                            >
                        </template>
                        <template v-else-if="filter.displayName === 'Groups'">
                            <GgmsChip
                                v-for="group in getLandingPageGroupsByFilterIds(filter)"
                                :key="group._id"
                                :bgColor="cachedLandingPageGroups[group._id]?.backgroundColor"
                                :textColor="cachedLandingPageGroups[group._id]?.foregroundColor"
                                :isRemovable="false"
                                >{{ group.displayName }}</GgmsChip
                            >
                        </template>
                        <template v-else>
                            <span v-if="checkIsArray(filter.value)">
                                <template v-if="filter.type === 'Date'">
                                    <span v-if="filter.value[0]" class="filter-value">{{
                                        displayFilterValue(
                                            filter.value[0],
                                            filter.field,
                                            true,
                                            shouldFormatTime(filter.displayName)
                                        )
                                    }}</span>
                                    <span v-if="filter.value[0] && filter.value[1]"> - </span>
                                    <span v-if="filter.value[1]" class="filter-value">{{
                                        displayFilterValue(
                                            filter.value[1],
                                            filter.field,
                                            true,
                                            shouldFormatTime(filter.displayName)
                                        )
                                    }}</span>
                                </template>
                                <template v-else-if="filter.rule === 'isBetween'">
                                    <span v-if="filter.value[0]" class="filter-value">{{
                                        displayFilterValue(filter.value[0], filter.field)
                                    }}</span>
                                    <span v-if="filter.value[0] && filter.value[1]">- </span>
                                    <span v-if="filter.value[1]" class="filter-value">
                                        {{ displayFilterValue(filter.value[1], filter.field) }}</span
                                    >
                                </template>
                                <template v-else>
                                    <span v-for="(value, index) in filter.value" :key="index" class="filter-value">{{
                                        displayFilterValue(value, filter.field)
                                    }}</span>
                                </template>
                            </span>
                            <span v-else-if="filter.field === 'doNotDisturb'">
                                {{ filter.value ? "is enabled" : "is not enabled" }}
                            </span>
                            <span v-else-if="filter.value" class="filter-value">
                                {{
                                    displayFilterValue(
                                        Array.isArray(filter.value) ? String(filter.value[0]) : String(filter.value),
                                        filter.field,
                                        filter.type === "Date",
                                        shouldFormatTime(filter.displayName),
                                        ["type", "typeName"].includes(filter.field),
                                        filter.rule === "isIn",
                                        filter.field === "reminder"
                                    )
                                }}
                            </span>
                        </template>
                    </div>
                </div>
                <div
                    v-if="iconVisibility[filterIndex] && !justDisplay"
                    class="flex gap-2 items-center flex-shrink-0 ml-2 mt-1"
                    style="max-width: 48px"
                >
                    <PencilIcon
                        class="h-5 w-5 text-gray-400 hover:text-gray-500"
                        @click.prevent.stop="editFilter(filter, filterIndex)"
                    />
                    <TrashIcon
                        v-if="filters.length > 1"
                        class="h-5 w-5 text-gray-400 hover:text-gray-500"
                        @click.prevent.stop="removeFilter(filterIndex)"
                    />
                </div>
            </div>
        </div>
        <div class="flex justify-between">
            <div
                v-if="!justDisplay"
                class="px-2 bg-primary-color-600 text-white rounded w-fit cursor-pointer"
                @click.stop="addNewFilterToGroup"
            >
                AND
            </div>
            <TrashIcon
                v-if="!isOnlyOneGroup && !justDisplay"
                class="h-5 w-5 text-gray-400 hover:text-gray-500 cursor-pointer"
                @click.stop="removeGroup"
            />
        </div>
    </div>
</template>

<script lang="ts" setup>
import { computed, ref } from "vue"
import GgmsChip from "@/components/GgmsChip.vue"
import { Filter } from "@/shared/models/filter"
import { displayFilterValue, displayFilterRule } from "@/shared/utils/helpers"
import { PencilIcon, TrashIcon } from "@heroicons/vue/outline"
import { useTagStore } from "@/stores/tag"
import { useOriginStore } from "@/stores/origin"
import { useLandingPageGroupStore } from "@/stores/landing-page-group"

interface Props {
    groupIndex: number
    filters: Filter[]
    groupsLength: number
    justDisplay?: boolean
}

const tagStore = useTagStore()
const originStore = useOriginStore()
const landingPageGroupStore = useLandingPageGroupStore()

const props = withDefaults(defineProps<Props>(), { justDisplay: false })
const emit = defineEmits(["removeGroup", "addNewFilterToGroup", "editFilter", "removeFilter"])

const iconVisibility = ref(new Array(props.filters.length).fill(false))

const isOnlyOneGroup = computed(() => props.groupsLength === 1)
const cachedTags = computed(() => tagStore.cachedTags)
const cachedOrigins = computed(() => originStore.cachedOrigins)
const cachedLandingPageGroups = computed(() => landingPageGroupStore.cachedGroups)

function getTagsByFilterIds(filter: Filter) {
    return tagStore.tableState.data.filter((tag) => (filter.value as string[])?.includes(tag._id))
}

function getOriginsByFilterIds(filter: Filter) {
    return originStore.tableState.data.filter((origin) => (filter.value as string[])?.includes(origin._id))
}

function shouldFormatTime(displayName?: string) {
    return [
        "Due Date & Time",
        "Last Updated At",
        "Created At",
        "Last Sent Email",
        "Last Email",
        "Last Received Email",
        "Last Call Made",
        "Last Call",
        "Last Call Received",
        "Last Sent Text",
        "Last Text",
        "Last Received Text",
        "Last Communication",
        "Sent at",
        "Last Run At",
        "Last Updated At",
    ].includes(displayName ?? "")
}

function getLandingPageGroupsByFilterIds(filter: Filter) {
    return landingPageGroupStore.tableState.data.filter((group) => (filter.value as string[])?.includes(group._id))
}

function removeGroup() {
    emit("removeGroup", props.groupIndex)
}

function addNewFilterToGroup() {
    emit("addNewFilterToGroup", props.groupIndex)
}

function removeFilter(filterIndex: number) {
    emit("removeFilter", props.groupIndex, filterIndex)
}

function editFilter(filter: Filter, filterIndex: number) {
    emit("editFilter", filter, props.groupIndex, filterIndex)
}

function setIconVisibility(filterIndex: number, isVisible: boolean) {
    iconVisibility.value[filterIndex] = isVisible
}

function checkIsArray(value?: string | string[] | Date) {
    return Array.isArray(value)
}
</script>

<style scoped>
.filter-value {
    @apply bg-primary-color-100 text-primary-color-500 px-1 py-0.5 rounded;
    word-break: break-word;
    white-space: normal;
    max-width: 100%;
    margin-right: 0.5rem;
}
</style>
